/**
 * Below the SURFACE edge the whole-file plot is the ground under the eye, and
 * it is a region bake: 7.3 by 25.8 km at Hadley, 11.4 by 18.6 at Jezero.
 * Everything past that used to be `DtmPlot`'s own surrounding plain, a flat
 * disc with three octaves of noise on it, because `setOrreryVisible(false)`
 * retired the pyramid and the globe at the same edge. So the Apennine front
 * stood across the horizon at PLOT and was gone one notch later, with a flat
 * rim in its place: the body ENDED at the bake's own footprint.
 *
 * The pyramid draws it now. The walkaround's rig is a local frame in
 * metres, registered with the world frame like `detailGroup`, so the two
 * share the body's own frame by construction rather than through a matrix
 * on `worldRoot`.
 */
import * as THREE from 'three';

import { devFlag, urlFlag, urlParam, TEST_SWITCHES } from '../urlFlags.ts';
import { directBeamShare } from '../world/bodies.ts';
import { SOLAR_RADIANCE_WM2SR } from './sol.ts';
import { SKY_EXPOSURE } from './atmoSky.ts';
import { oldAir } from './atmosphere.ts';
import { EXPOSURE_CALIBRATION_BODY } from './exposure.ts';
import type { PlotMeta } from './plotField';
import type { ZoomLadderHandle } from './zoomLadder';
import type { PyramidViewHandle } from './pyramidView';
import type { CatalogueStarsHandle } from '../scene/sky';
import type { AtlasLive, AtlasState } from '../atlas';
import type { AtmosphereViewHandle } from './atmosphereView.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { InjectsHandle } from './injects.ts';
import type { MountHandle } from './mount.ts';
import type { PlotMountHandle } from './plotMount.ts';
import type { PlotSunHandle } from './plotSun.ts';
import type { SolarScrubHandle } from './solarScrub.ts';
import type { SurfaceRadiusHandle } from './surfaceRadius.ts';

/** `?blankcanvas=1` clears the Atlas's own frame instead of drawing it - the
 * blank canvas a not-black reading of `.atlas-canvas` has to be able to
 * catch. DEV-only, the way the flags below are. */
const canvasBlankActive = devFlag(() => TEST_SWITCHES && urlFlag('blankcanvas'));

/** `?transposedbasis=1` transposes the plot's site basis before it is used
 * to place the walkaround mesh - the perturbation `surface-clamp.mjs`'s
 * `--stand --inject transposed-basis` control reproduces, rather than a
 * hand-edit of `syncPlotFrame()`. `?transposedbasistwistdeg=<deg>` turns it
 * by <deg> degrees about the site's own east axis instead of the full
 * transpose, a separate flag rather than an overload of the same one so a
 * literal 1-degree twist can never collide with the `=1` full-transpose
 * sentinel; `--twist-deg` drives it to read the smallest twist the range
 * gate still catches. DEV-only: absent from the shipped JavaScript and HTML
 * a build carries, the same as `canvasBlankActive` above - a production
 * source map still carries every source line, this one included, by the
 * same `vite.config.ts` setting neither flag touches. */
const transposedSiteBasis = devFlag(() => TEST_SWITCHES && urlFlag('transposedbasis'));
const twistDeg = devFlag(() => TEST_SWITCHES && Number(urlParam('transposedbasistwistdeg')));
function transposedBasisDeg(): number {
  return twistDeg() || 0;
}

/** The sun's disc from the ground, in the units the sky over it is drawn in. */
const GROUND_SUN_RADIANCE = SOLAR_RADIANCE_WM2SR * SKY_EXPOSURE;

/**
 * Sines of the sun's depression under the walkaround's horizon over which the
 * star field comes out: none at 6 degrees, all of it by 12, the ends of civil
 * and nautical twilight. The field is drawn in display terms and not in the
 * sky's radiance, so nothing else takes it out of a daylit sky.
 */
const STARS_OUT_SIN: readonly [number, number] = [Math.sin((6 * Math.PI) / 180), Math.sin((12 * Math.PI) / 180)];

export interface FarFieldDeps {
  readonly mount: MountHandle;
  readonly plotMount: PlotMountHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly plotSun: PlotSunHandle;
  readonly cameraRig: CameraRigHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly bodyScene: BodySceneHandle;
  readonly injects: InjectsHandle;
  readonly live: AtlasLive;
  readonly renderer: THREE.WebGLRenderer;
  readonly stars: CatalogueStarsHandle;
  readonly state: AtlasState;
  readonly ladder: ZoomLadderHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly atmosphereView: AtmosphereViewHandle;
}

export interface FarFieldHandle {
  syncPlotFrame(): boolean;
  placePlotNode(): void;
  plotFrameToWorld(v: THREE.Vector3): THREE.Vector3;
  plotDirToWorld(v: THREE.Vector3): THREE.Vector3;
  worldDirToPlot(v: THREE.Vector3): THREE.Vector3;
  drawFrame(): void;
  flatnessLimitM(): number;
  farFieldWanted(): boolean;
  placeFarField(): void;
  syncGroundSky(): void;
  readonly farFieldDrawn: boolean;
  readonly csmBakeCasters: number;
  readonly farPassCalls: number;
  legacyHandover: boolean;
  edgeMarsCurve: boolean;
  edgeRockFloorOff: boolean;
  legacyPassHide: boolean;
}

export function createFarField(deps: FarFieldDeps): FarFieldHandle {
  const { renderer, stars, state, ladder, pyramidView } = deps;
  const { camera, scene, atlasRoot, worldRoot } = deps.mount;
  const { dtmGroup, plotQuat, plotUpWorld, plotWorld } = deps.plotMount;
  const { leafDatumM } = deps.surfaceRadius;
  const { plotSunDirLocal } = deps.plotSun;
  const { camPos, inLocalSky, ladderOwnsFrame, surfaceRegime, lookUpDebug } = deps.cameraRig;
  const { solar } = deps.solarScrub;

  /** `surface-clamp --edge --inject legacy-handover`: the far field retired again. */
  let legacyHandover = false;
  /** `surface-clamp --edge --inject mars-curve`: the pre-fix tint levelling back. */
  let edgeMarsCurve = false;
  /** `surface-clamp --edge --inject speckle`: the scatter with no draw floor. */
  let edgeRockFloorOff = false;
  /** Whether the last placed frame put the body's own leaves behind the plot. */
  let farFieldDrawn = false;

  const _farE = new THREE.Vector3();
  const _farU = new THREE.Vector3();
  const _farS = new THREE.Vector3();
  const _farO = new THREE.Vector3();
  const _plotBasis = new THREE.Matrix4();
  const _twistBasis = new THREE.Matrix4();
  const _plotV = new THREE.Vector3();
  const _qInv = new THREE.Quaternion();

  /**
   * The site's own east/up/south basis, body-fixed, into `_farE`/`_farU`/`_farS`.
   *
   * `latLonToVec3`'s convention, so east is d/dlon and south is -d/dlat, both
   * normalised. The triple is RIGHT-handed - at lat 0 lon 0 it reads
   * (0, 0, -1), (1, 0, 0), (0, -1, 0) and east cross up is south - so
   * `makeBasis` of it is a rotation with determinant +1 that maps the
   * region's axes out into the body's, which is what the registration wants.
   * The transposed, inverse sense would mirror the plot about its own centre,
   * and `--handover` is what reads it: it measures the leaves against the
   * drawn mesh at one lat/lon, so a mirrored plot puts the two kilometres
   * apart in `fields`.
   */
  function siteBasis(meta: PlotMeta): void {
    const lat = THREE.MathUtils.degToRad(meta.center_lat_deg);
    const lon = THREE.MathUtils.degToRad(meta.center_lon_deg);
    const cl = Math.cos(lat);
    const sl = Math.sin(lat);
    const co = Math.cos(lon);
    const so = Math.sin(lon);
    _farE.set(-so, 0, -co);
    _farU.set(cl * co, sl, -cl * so);
    _farS.set(sl * co, -cl, -sl * so);
    // The leaves' datum and not the registry's: the two differ by 608 m on
    // Mars, and the surface the plot has to meet is the one drawn.
    _farO.copy(_farU).multiplyScalar(leafDatumM() + meta.elevation.min_m * solar.exag);
  }

  /**
   * Where the plot's own frame stands in the world, for the frame about to draw.
   *
   * The region bake's local frame is the equirectangular grid about its centre:
   * +x east, +z south, +y up, and local y = 0 is the file's MINIMUM elevation.
   * The walkaround's frame is that translated by the group's own recentre on
   * the cluster centroid, so the frame's origin is the point of the region's
   * plane under that centroid and the map out to body-fixed metres is the site
   * basis about the datum point under the region's centre.
   *
   * Composed in doubles and handed over as a position and a quaternion, the way
   * `detailGroup`'s are, rather than as a matrix on the world root: the
   * translation is a body radius and the residual the frame is drawn at is
   * metres.
   */
  function syncPlotFrame(): boolean {
    const node = deps.bodyScene.state.bodyNode;
    const meta = deps.live.plotField?.meta ?? null;
    const plot = deps.live.plot;
    if (!node || !meta || !plot) {
      // A walkaround with no mesh under it never leaves the focus rig: the
      // identity is what the eye and the sky are measured in there, and a
      // stale basis here would tilt both.
      plotQuat.identity();
      plotUpWorld.set(0, 1, 0);
      return false;
    }
    siteBasis(meta);
    _plotBasis.makeBasis(_farE, _farU, _farS);
    if (transposedSiteBasis()) {
      _plotBasis.transpose();
    } else if (transposedBasisDeg()) {
      // A smaller, parameterised sibling of the full transpose above: tips
      // the basis about its own EAST axis by a given angle - carrying up
      // toward south - instead of swapping it outright, so `--stand --inject
      // transposed-basis --twist-deg <n>` can read the smallest twist
      // `ANCHOR_RANGE_MAX_M` still catches. A twist about the up axis alone
      // leaves the group's own vertical offset almost unmoved and reads
      // under a degree of the clean baseline at every angle; a twist about
      // east moves the same term the transpose does.
      _twistBasis.makeRotationAxis(_farE, THREE.MathUtils.degToRad(transposedBasisDeg()));
      _plotBasis.premultiply(_twistBasis);
    }
    plotQuat.setFromRotationMatrix(_plotBasis).premultiply(node.quaternion);
    plotUpWorld.set(0, 1, 0).applyQuaternion(plotQuat);
    // The whole of the group's own offset, `liftMeshM`'s step included, so the
    // mesh and the leaves move together under that control exactly as they did
    // when the far field's matrix carried the same offset.
    _plotV.copy(plot.group.position).negate()
      .applyMatrix4(_plotBasis).add(_farO).applyQuaternion(node.quaternion);
    plotWorld.x = node.world.x + _plotV.x;
    plotWorld.y = node.world.y + _plotV.y;
    plotWorld.z = node.world.z + _plotV.z;
    dtmGroup.quaternion.copy(plotQuat);
    return true;
  }

  /**
   * The plot's drawn placement, ahead of the world frame's own pass.
   *
   * `applyCameraRelative` writes the same position at the top of the draw; this
   * is here so that everything between the camera solve and that pass - the
   * screen projections, the ray picks, the cascades - reads a group matrix that
   * agrees with the eye this tick rather than the one before it.
   */
  function placePlotNode(): void {
    dtmGroup.position.copy(camPos).applyQuaternion(plotQuat).negate();
    dtmGroup.updateMatrixWorld(true);
  }

  /** A plot-frame point into the drawn frame, which has the eye at its origin. */
  function plotFrameToWorld(v: THREE.Vector3): THREE.Vector3 {
    return v.sub(camPos).applyQuaternion(plotQuat);
  }

  /** The same for a direction, where the eye's own offset does not apply. */
  function plotDirToWorld(v: THREE.Vector3): THREE.Vector3 {
    return v.applyQuaternion(plotQuat);
  }

  /** And back: a drawn-frame direction read in the plot's own metres. */
  function worldDirToPlot(v: THREE.Vector3): THREE.Vector3 {
    return v.applyQuaternion(_qInv.copy(plotQuat).invert());
  }

  /** Everything the second pass hides, so the list is allocated once. */
  const _passHidden: THREE.Object3D[] = [];

  /** Casters the last cascade bake could actually see, for `shadowDebug`:
   *  zero is three empty depth maps and a ground with nothing on it. */
  let csmBakeCasters = -1;
  /** Draw calls the second pass issued: zero is a pass that drew nothing. */
  let farPassCalls = -1;
  /** `--shadow --inject subtree-hide`: the pre-fix hide, over the SCENE's own
   *  children rather than the mesh's siblings, back. */
  let legacyPassHide = false;

  /** Each level between the mesh and the Atlas subtree: what to keep, and among what. */
  function passHideLevels(): Array<{ keep: THREE.Object3D; among: THREE.Object3D[] }> {
    const levels: Array<{ keep: THREE.Object3D; among: THREE.Object3D[] }> = [];
    for (let node: THREE.Object3D = dtmGroup; node.parent !== null; node = node.parent) {
      levels.push({ keep: node, among: node.parent.children });
      if (node.parent === atlasRoot) break;
    }
    return levels;
  }

  /** Whether three's own walk reaches this object: every ancestor visible too. */
  function reachable(o: THREE.Object3D): boolean {
    for (let p: THREE.Object3D | null = o; p !== null; p = p.parent) if (!p.visible) return false;
    return true;
  }

  function countCasters(): number {
    let n = 0;
    // The whole ancestor chain, not `dtmGroup.visible` alone: with the subtree
    // above it hidden this counted 116 casters into a bake that saw none.
    dtmGroup.traverse((o) => {
      if ((o as THREE.Mesh).isMesh && o.castShadow && reachable(o)) n++;
    });
    return n;
  }

  /**
   * One draw above the SURFACE edge; two below it, with the depth buffer cut.
   *
   * The leaves are 33 vertices across a 2.7 km tile at Hadley's deepest level
   * and the plot's lattice is a post every 17.6 m over the same ground, so
   * inside the bake's footprint the two surfaces cross each other by tens of
   * metres in both directions. Sharing one depth buffer that reads as the
   * coarse mesh tearing through the fine one. Cutting depth between the passes
   * states the rule instead: the measured mesh owns every pixel it covers, the
   * leaves own the rest. The plot's surrounding plain and its plinth are off
   * with the far field on (`DtmPlot.setFarField`), or they would own their own
   * pixels under the same rule.
   */
  function drawFrame(): void {
    deps.injects.state.frameTick++;
    if (canvasBlankActive()) {
      renderer.clear();
      return;
    }
    if (deps.injects.state.greyFramePending) {
      deps.injects.state.greyFramePending = false;
      renderer.clear();
      return;
    }
    if (!farFieldDrawn) {
      if (renderer.shadowMap.needsUpdate) csmBakeCasters = countCasters();
      renderer.render(scene, camera);
      return;
    }
    // Every caster hangs off `plot.group` inside `dtmGroup` - the rover and the
    // rock scatter - and the three cascade lights are scene children outside it,
    // so neither pass on its own can bake a cascade. Three's shadow pass runs at
    // the top of every `render` and spends `needsUpdate` there, so the first pass
    // was baking three empty maps with the casters hidden and the second, which
    // had already lost the flag, drew the plot against them. Hold the flag over
    // the first pass and hand the bake to the second.
    const shadowUpdate = renderer.shadowMap.needsUpdate;
    renderer.shadowMap.needsUpdate = false;
    dtmGroup.visible = false;
    renderer.render(scene, camera);
    dtmGroup.visible = true;
    _passHidden.length = 0;
    // The mesh's own SIBLINGS at every level up to the subtree, not the scene's
    // children: `dtmGroup` hangs off the Atlas subtree, so `child === dtmGroup`
    // matched no scene child and the pass hid `world-root` - the subtree the
    // mesh itself is inside. Under the depth cut that drew an empty scene: no
    // plot mesh, no rover, no rock, and a cascade bake at the top of this
    // render with every caster out of reach. Walked up the chain because the
    // registration put `dtmGroup` INSIDE the world root, where hiding one level
    // would leave the leaves drawn over the mesh the cut exists to give the
    // pixels to.
    const hideIn = legacyPassHide ? [{ keep: dtmGroup, among: scene.children }]
      : passHideLevels();
    for (const level of hideIn) {
      for (const child of level.among) {
        // The lights stay visible through the second pass: three drops an
        // invisible light from the shadow list, and a light draws nothing itself.
        if (child === level.keep || !child.visible || child instanceof THREE.Light) continue;
        child.visible = false;
        _passHidden.push(child);
      }
    }
    const autoClear = renderer.autoClear;
    const shadowAuto = renderer.shadowMap.autoUpdate;
    // `WebGLBackground.render` forces a clear of its own whenever a scene
    // carries a Colour background, whatever `autoClear` says (three 0.170,
    // build/three.cjs), so the second pass was wiping the first: the far field
    // was drawn and then erased every frame, and everything past the bake's
    // own footprint read as the background rather than as the leaves. Nulling
    // it for the pass leaves the clear to `clearDepth` below, which is the one
    // buffer the two passes are meant to cut.
    const background = scene.background;
    scene.background = null;
    renderer.autoClear = false;
    renderer.shadowMap.autoUpdate = false;
    renderer.shadowMap.needsUpdate = shadowUpdate;
    if (shadowUpdate) csmBakeCasters = countCasters();
    renderer.clearDepth();
    renderer.render(scene, camera);
    farPassCalls = renderer.info.render.calls;
    scene.background = background;
    renderer.autoClear = autoClear;
    renderer.shadowMap.autoUpdate = shadowAuto;
    for (const child of _passHidden) child.visible = true;
    _passHidden.length = 0;
  }

  /**
   * How far the plot's own mesh may own its pixels, metres from the bake's centre.
   *
   * The region bake is a PLANE and the leaves are the sphere, tangent at the
   * bake's centre (`placeFarField` below), so the mesh stands d^2/2R over the
   * ground outside it and that error moves the ground on screen by d/2R
   * radians. Out to where that reaches one pixel of the frame the person is
   * looking at, the measured mesh is the better answer; past it the plane is
   * drawing a horizon the body does not have, and at Hadley it painted its own
   * smooth far end over the leaves' skyline in 18 per cent of columns. The
   * frame's own CSS height and not the drawing buffer's, so the governor
   * dropping `renderScale` does not move the ground.
   */
  function flatnessLimitM(): number {
    const rows = Math.max(1, renderer.domElement.clientHeight);
    const pxRad = (2 * Math.tan((camera.fov * Math.PI) / 360)) / rows;
    return 2 * leafDatumM() * pxRad;
  }

  /** The band, a mesh to stand on, and a pyramid on the same body to stand it in. */
  function farFieldWanted(): boolean {
    return ladderOwnsFrame() && !legacyHandover && surfaceRegime() && deps.live.plot !== null
      && deps.live.plotField !== null && deps.bodyScene.state.bodyNode !== null && pyramidView.tileBody !== null
      && pyramidView.tileBody === state.bodyId;
  }

  /**
   * Hold the leaves and the plot mesh in one frame, for the frame about to draw.
   *
   * The plot is a registered node of the world frame now, so the two share the
   * body's own frame by construction and what is left here is the handover's
   * other half: the flatness cut the mesh may own its pixels out to, the
   * orrery's decoration, and whether the world root is drawn at all.
   *
   * What the linearisation costs is the region's own flatness, in either
   * frame: the bake is a plane and the leaves are the sphere, so the mesh's
   * edge stands d^2/2R over the ground outside it - 3.7 m at Hadley's 3.6 km
   * sides and 48 m at its 12.9 km ends, a step DOWN at the boundary and never
   * a hole.
   */
  function placeFarField(): void {
    const on = farFieldWanted();
    if (on !== farFieldDrawn) {
      farFieldDrawn = on;
      deps.live.needsRender = true;
    }
    deps.live.plot?.setFarField(on, flatnessLimitM());
    // `dtmGroup` hangs under the world root once the plot is registered, so a
    // walkaround with no leaves behind it - a body whose pyramid has not landed,
    // or `--inject legacy-handover` - would otherwise take the mesh down with
    // the root it is drawn inside.
    worldRoot.visible = ladder.orreryOn || on || pyramidView.farFieldHeld || dtmGroup.visible;
    const orrery = deps.bodyScene.state.orrery;
    if (!on || !orrery) return;
    // What comes back is the ground - the leaves and the focused body's globe
    // under them - the light that shades it, and the sky over it: the sun's
    // disc and the focused body's own system, where the registry puts them.
    // The rest of the orrery's bodies stay retired.
    for (const row of deps.bodyScene.state.bodyRows) {
      const b = orrery.byId(row.id);
      if (b && b !== deps.bodyScene.state.bodyNode && (lookUpDebug.skyHidden || !inLocalSky(row))) b.mesh.visible = false;
    }
    if (lookUpDebug.skyHidden) orrery.sol.mesh.visible = false;
    for (const l of orrery.lights) {
      l.visible = true;
    }
  }

  /**
   * What the walkaround's sky keeps of the orrery's own: the star field, which
   * is directions alone and as true from the ground as from anywhere, out as
   * the sun goes down, and the sun as the photosphere's white disc rather than
   * the 304 A map. The rest - the other bodies at the orrery's angular floor,
   * their labels and orbit lines - stays retired with the orrery
   * (`placeFarField`). The disc is in the hue the air it is seen through
   * leaves of it, white where there is none or the layer is off.
   */
  function syncGroundSky(): void {
    const fade = ladder.orreryOn || lookUpDebug.dayStars
      ? 1 : THREE.MathUtils.smoothstep(-plotSunDirLocal.y, STARS_OUT_SIN[0], STARS_OUT_SIN[1]);
    stars.setFade(fade);
    stars.points.visible = (ladder.orreryOn || !lookUpDebug.starsHidden) && fade > 0;
    deps.bodyScene.state.orrery?.sol.setGroundView(
      ladder.orreryOn || lookUpDebug.sunUv ? null : GROUND_SUN_RADIANCE, groundSunTransmittance());
    const tint = groundLightTint();
    const share = groundLightOwned() ? beamShare() : 1;
    if (share < 1) {
      // Where the column takes the beam, the fill carries what is left: the
      // diffuse flux's own hue, falling with the sun's height.
      const mu = Math.max(deps.atmosphereView.atmoSunMu(), 0);
      const hue = overcastHue(tint);
      _fillT.set(hue.x * mu, hue.y * mu, hue.z * mu).multiplyScalar(1 - share).addScalar(share);
      tint.multiplyScalar(share);
      // Under a deck the ground sees the dome atmoDeck draws, floor/pi * muSun
      // of the sun's irradiance, so it is lit at its albedo times that dome.
      const floor = floorFluxMemo;
      const k = floor ? (mu * (1 - share) * (deps.atmosphereView.atmoTerrainUniforms['uAtmoSunIrradiance']!.value as number)) / Math.PI : 0;
      _skyLightT.set(floor ? floor[0] * k : 0, floor ? floor[1] * k : 0, floor ? floor[2] * k : 0);
    } else {
      _fillT.set(1, 1, 1);
      _skyLightT.set(0, 0, 0);
    }
    deps.live.plot?.setSunTint(tint);
    deps.live.plot?.setFillTint(_fillT, _skyLightT);
    deps.bodyScene.state.orrery?.setSunTint(ladder.orreryOn ? null : tint);
    // The far ground (leaves and globe) and the haze in front of it read the
    // same dome, through uAtmoDeckSkyLight: descent-dojo --inject unlit-far
    // puts them back on the orrery's fake ambient alone, which is what main
    // draws today.
    const farSkyLight = deps.atmosphereView.atmoTerrainUniforms['uAtmoDeckSkyLight']!.value as THREE.Vector3;
    if (lookUpDebug.unlitFar) farSkyLight.set(0, 0, 0);
    else farSkyLight.copy(_skyLightT);
  }

  const _fillT = new THREE.Vector3();
  const _skyLightT = new THREE.Vector3();
  const _hueT = new THREE.Vector3();
  /** The diffuse light's hue under the air: a deck's own floor flux where it has one, the beam's hue otherwise. */
  function overcastHue(beam: THREE.Vector3): THREE.Vector3 {
    beamShare();
    const floor = floorFluxMemo;
    const peak = floor ? Math.max(...floor) : 0;
    return floor && peak > 0 ? _hueT.set(floor[0] / peak, floor[1] / peak, floor[2] / peak) : _hueT.copy(beam);
  }

  let shareMemoId: string | null | undefined;
  let shareMemo = 1;
  let floorFluxMemo: readonly [number, number, number] | undefined;
  /** The focused body's `directBeamShare` against the exposure's calibration air; 1 under `?oldair=1`. */
  function beamShare(): number {
    if (oldAir()) return 1;
    if (shareMemoId === state.bodyId) return shareMemo;
    const rows = deps.bodyScene.state.bodyRows;
    const here = rows.find((r) => r.id === state.bodyId)?.atmosphere;
    const ref = rows.find((r) => r.id === EXPOSURE_CALIBRATION_BODY)?.atmosphere;
    if (!here || !ref) return 1;
    shareMemo = here.kind === 'data' && ref.kind === 'data' ? directBeamShare(here, ref) : 1;
    floorFluxMemo = here.kind === 'data' ? here.deck_floor_flux : undefined;
    shareMemoId = state.bodyId;
    return shareMemo;
  }

  /** Whether the mounted air is the focused body's and its light is not held white. */
  function groundLightOwned(): boolean {
    const view = deps.atmosphereView;
    return !lookUpDebug.whiteLight && !!view.atmo && view.atmoLayerOn && view.atmoBodyId === state.bodyId;
  }

  const _beamT = new THREE.Vector3();
  const _zenithT = new THREE.Vector3();
  /** The ground's sunlight: what the air leaves of the beam over what it leaves overhead, at its brightest channel's level. */
  function groundLightTint(): THREE.Vector3 {
    const atmo = deps.atmosphereView.atmo;
    if (!atmo || !groundLightOwned()) {
      return _beamT.set(1, 1, 1);
    }
    atmo.sunTransmittance(deps.atmosphereView.atmoSunMu(), _beamT);
    atmo.sunTransmittance(1, _zenithT);
    _beamT.set(_beamT.x / Math.max(_zenithT.x, 1e-9), _beamT.y / Math.max(_zenithT.y, 1e-9),
      _beamT.z / Math.max(_zenithT.z, 1e-9));
    if (lookUpDebug.noGreenTint) _beamT.y = _beamT.x;
    const peak = Math.max(_beamT.x, _beamT.y, _beamT.z);
    return peak > 0 ? _beamT.divideScalar(peak) : _beamT.set(1, 1, 1);
  }

  const _sunT = new THREE.Vector3();
  function groundSunTransmittance(): THREE.Vector3 {
    const view = deps.atmosphereView;
    const atmo = view.atmo;
    if (lookUpDebug.whiteSun || !atmo || !view.atmoLayerOn || view.atmoBodyId !== state.bodyId) {
      return _sunT.set(1, 1, 1);
    }
    return atmo.sunTransmittance(view.atmoSunMu(), _sunT);
  }

  return {
    syncPlotFrame,
    placePlotNode,
    plotFrameToWorld,
    plotDirToWorld,
    worldDirToPlot,
    drawFrame,
    flatnessLimitM,
    farFieldWanted,
    placeFarField,
    syncGroundSky,
    get farFieldDrawn() { return farFieldDrawn; },
    get csmBakeCasters() { return csmBakeCasters; },
    get farPassCalls() { return farPassCalls; },
    get legacyHandover() { return legacyHandover; },
    set legacyHandover(v) { legacyHandover = v; },
    get edgeMarsCurve() { return edgeMarsCurve; },
    set edgeMarsCurve(v) { edgeMarsCurve = v; },
    get edgeRockFloorOff() { return edgeRockFloorOff; },
    set edgeRockFloorOff(v) { edgeRockFloorOff = v; },
    get legacyPassHide() { return legacyPassHide; },
    set legacyPassHide(v) { legacyPassHide = v; },
  };
}
